import {
  KeyMoment,
  KeywordStat,
  KeywordTheme,
  SpeakerTone,
  SpeechSegment,
  ThemeKeyword,
  ToneSegment,
} from '../../../services/api.service';
import { canonicalizeSpeakerDisplayName } from '../../../utils/speaker-display-names';
import {
  formatSpeakerFullName,
  formatSpeakerShortName,
  formatSpeakerTrackName,
  getSegmentEndSec,
  getSegmentStartSec,
  getSpeakerName,
  stripSpeakerOrgSuffix,
} from './audio-segments';

export type ActivityBucket = {
  startSec: number;
  endSec: number;
  total: number;
  bySpeaker: { name: string; color: string; count: number }[];
};

export type ReplicaStat = {
  name: string;
  displayName: string;
  color: string;
  count: number;
};

export type InterruptionStat = {
  from: string;
  to: string;
  fromDisplay: string;
  toDisplay: string;
  fromFull: string;
  toFull: string;
  fromColor: string;
  toColor: string;
  count: number;
};

export type TonePeriod = {
  label: string;
  startSec: number;
  endSec: number;
  segments: { tone: string; widthPct: number }[];
  moodLabel: string;
};

const KTZH_PRIMARY = '#1c9bd7';
const KTZH_PRIMARY_DEEP = '#1378ae';
const KTZH_PRIMARY_LIGHT = '#e4f1f9';

const TONE_COLORS: Record<string, string> = {
  neutral: '#14b8a6',
  constructive: KTZH_PRIMARY,
  tense: '#f59e0b',
};

const MOOD_LABELS: Record<string, string> = {
  neutral: 'нейтрально',
  constructive: 'конструктивно',
  tense: 'напряжение',
};

export function buildActivityBuckets(
  segments: SpeechSegment[],
  speakerColors: Record<string, string>,
  durationSec: number,
  bucketCount = 24,
): ActivityBucket[] {
  const bucketSize = durationSec > 0 ? durationSec / bucketCount : 1;
  const buckets: ActivityBucket[] = Array.from({ length: bucketCount }, (_, i) => ({
    startSec: i * bucketSize,
    endSec: (i + 1) * bucketSize,
    total: 0,
    bySpeaker: [],
  }));

  const speakerCounts: Record<number, Record<string, number>> = {};

  segments.forEach(seg => {
    const start = getSegmentStartSec(seg);
    const idx = Math.min(bucketCount - 1, Math.floor(start / bucketSize));
    const name = getSpeakerName(seg);
    buckets[idx].total += 1;
    if (!speakerCounts[idx]) speakerCounts[idx] = {};
    speakerCounts[idx][name] = (speakerCounts[idx][name] || 0) + 1;
  });

  buckets.forEach((bucket, idx) => {
    bucket.bySpeaker = Object.entries(speakerCounts[idx] || {})
      .map(([name, count]) => ({
        name,
        color: speakerColors[name] || '#a3a3a3',
        count,
      }))
      .sort((a, b) => b.count - a.count);
  });

  return buckets;
}

export function buildReplicaStats(
  segments: SpeechSegment[],
  speakerTimelines: { name: string; color: string }[],
): ReplicaStat[] {
  const counts: Record<string, number> = {};
  segments.forEach(seg => {
    const name = getSpeakerName(seg);
    counts[name] = (counts[name] || 0) + 1;
  });

  return speakerTimelines.map(s => ({
    name: s.name,
    displayName: formatSpeakerFullName(s.name),
    color: s.color,
    count: counts[s.name] || 0,
  }));
}

export type InterruptionMoment = {
  atSec: number;
  /** Кто перебил */
  from: string;
  /** Кого перебили */
  to: string;
};

/** Моменты перебиваний с привязкой ко времени — для меток-шипов на ленте встречи. */
export function buildInterruptionMoments(
  segments: SpeechSegment[],
  gapThresholdSec = 1.5,
): InterruptionMoment[] {
  const sorted = [...segments].sort((a, b) => getSegmentStartSec(a) - getSegmentStartSec(b));
  const moments: InterruptionMoment[] = [];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    const prevName = getSpeakerName(prev);
    const currName = getSpeakerName(curr);
    if (prevName === currName) continue;

    const gap = getSegmentStartSec(curr) - getSegmentEndSec(prev);
    if (gap <= gapThresholdSec) {
      moments.push({ atSec: getSegmentStartSec(curr), from: currName, to: prevName });
    }
  }

  return moments;
}

export function buildInterruptions(
  segments: SpeechSegment[],
  speakerColors: Record<string, string>,
  gapThresholdSec = 1.5,
): InterruptionStat[] {
  const counts: Record<string, number> = {};
  buildInterruptionMoments(segments, gapThresholdSec).forEach(moment => {
    const key = `${moment.from}→${moment.to}`;
    counts[key] = (counts[key] || 0) + 1;
  });

  return Object.entries(counts)
    .map(([key, count]) => {
      const [from, to] = key.split('→');
      return {
        from,
        to,
        fromDisplay: formatSpeakerShortName(from),
        toDisplay: formatSpeakerShortName(to),
        fromFull: formatSpeakerFullName(from),
        toFull: formatSpeakerFullName(to),
        fromColor: speakerColors[from] || '#a3a3a3',
        toColor: speakerColors[to] || '#a3a3a3',
        count,
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);
}

export function buildTonePeriods(
  toneSegments: ToneSegment[],
  durationSec: number,
  periodCount = 4,
  keyMoments: KeyMoment[] = [],
): TonePeriod[] {
  if (!durationSec || !toneSegments.length) return [];

  const chunk = durationSec / periodCount;
  return Array.from({ length: periodCount }, (_, i) => {
    const startSec = i * chunk;
    const endSec = (i + 1) * chunk;
    const toneWeights: Record<string, number> = { neutral: 0, constructive: 0, tense: 0 };

    toneSegments.forEach(seg => {
      const overlapStart = Math.max(startSec, seg.start);
      const overlapEnd = Math.min(endSec, seg.end);
      if (overlapEnd > overlapStart) {
        toneWeights[seg.tone] = (toneWeights[seg.tone] || 0) + (overlapEnd - overlapStart);
      }
    });

    const periodMoments = keyMoments.filter(m => m.time >= startSec && m.time < endSec);
    const conflictCount = periodMoments.filter(m => m.tag_type === 'conflict').length;
    const disputeCount = periodMoments.filter(m => m.tag_type === 'dispute').length;
    if (conflictCount > 0 || disputeCount > 0) {
      toneWeights.tense += chunk * (0.25 + conflictCount * 0.15 + disputeCount * 0.1);
    }

    const total = Object.values(toneWeights).reduce((s, v) => s + v, 0) || 1;
    const segments = (['neutral', 'constructive', 'tense'] as const)
      .filter(t => toneWeights[t] > 0)
      .map(tone => ({
        tone,
        widthPct: (toneWeights[tone] / total) * 100,
      }));

    const dominant = Object.entries(toneWeights).sort((a, b) => b[1] - a[1])[0]?.[0] || 'neutral';
    let moodLabel = MOOD_LABELS[dominant] || 'нейтрально';
    if (conflictCount > 0) moodLabel = 'конфликт';
    else if (disputeCount > 0) moodLabel = 'спор';
    else if (dominant === 'tense' && toneWeights.tense > total * 0.35) moodLabel = 'напряжение';
    else if (dominant === 'constructive' && i === periodCount - 1) moodLabel = 'решения';

    const startMin = Math.floor(startSec / 60);
    const endMin = Math.ceil(endSec / 60);
    return {
      label: `${startMin}–${endMin} мин`,
      startSec,
      endSec,
      segments,
      moodLabel,
    };
  });
}

export function getToneColor(tone: string): string {
  return TONE_COLORS[tone] || TONE_COLORS.neutral;
}

export function getSpeakerToneLabel(tone: string): string {
  return MOOD_LABELS[tone] || MOOD_LABELS.neutral;
}

export function resolveSpeakerColor(
  speakerName: string,
  speakerTimelines: { name: string; color: string }[],
): string {
  const exact = speakerTimelines.find(s => s.name === speakerName);
  if (exact) return exact.color;
  const lower = normSpeakerName(speakerName);
  const match = speakerTimelines.find(s => (
    normSpeakerName(s.name) === lower
    || formatSpeakerTrackName(s.name).toLowerCase() === lower
  ));
  return match?.color || '#a3a3a3';
}

export function resolveSpeakerShortName(
  speakerName: string,
  speakerTimelines: { name: string; color: string }[],
): string {
  const resolved = resolveSpeakerTimelineName(speakerName, speakerTimelines);
  return formatSpeakerShortName(resolved);
}

export function resolveSpeakerFullName(
  speakerName: string,
  speakerTimelines: { name: string; color: string }[],
): string {
  const resolved = resolveSpeakerTimelineName(speakerName, speakerTimelines);
  return formatSpeakerFullName(resolved);
}

function normSpeakerName(name: string): string {
  return canonicalizeSpeakerDisplayName(stripSpeakerOrgSuffix(name)).toLowerCase();
}

function resolveSpeakerTimelineName(
  speakerName: string,
  speakerTimelines: { name: string; color: string }[],
): string {
  const exact = speakerTimelines.find(s => s.name === speakerName);
  if (exact) return exact.name;
  const lower = normSpeakerName(speakerName);
  const match = speakerTimelines.find(s => (
    normSpeakerName(s.name) === lower
    || formatSpeakerTrackName(s.name).toLowerCase() === lower
  ));
  return match?.name ?? canonicalizeSpeakerDisplayName(speakerName);
}

export function resolveSpeakerDisplayName(
  speakerName: string,
  speakerTimelines: { name: string; color: string }[],
): string {
  return resolveSpeakerShortName(speakerName, speakerTimelines);
}

export function sortSpeakerTones(
  speakerTones: SpeakerTone[],
  speakerTimelines: { name: string; color: string }[],
): SpeakerTone[] {
  const order = new Map(speakerTimelines.map((s, i) => [s.name, i]));
  return [...speakerTones].sort((a, b) => {
    const ai = order.get(a.speaker) ?? 999;
    const bi = order.get(b.speaker) ?? 999;
    if (ai !== bi) return ai - bi;
    return (b.score ?? 0) - (a.score ?? 0);
  });
}

export type MomentTagStyle = {
  bg: string;
  text: string;
  border: string;
  dot: string;
};

export const MOMENT_TAG_STYLES: Record<string, MomentTagStyle> = {
  task: {
    bg: '#dbeafe',
    text: '#1e40af',
    border: '#93c5fd',
    dot: '#2563eb',
  },
  dispute: {
    bg: '#fef3c7',
    text: '#92400e',
    border: '#fcd34d',
    dot: '#d97706',
  },
  conflict: {
    bg: '#fee2e2',
    text: '#991b1b',
    border: '#fca5a5',
    dot: '#dc2626',
  },
  decision: {
    bg: KTZH_PRIMARY_LIGHT,
    text: KTZH_PRIMARY_DEEP,
    border: 'rgba(28, 155, 215, 0.35)',
    dot: KTZH_PRIMARY_DEEP,
  },
  action: {
    bg: '#ccfbf1',
    text: '#115e59',
    border: '#5eead4',
    dot: '#0d9488',
  },
};

/** @deprecated use getMomentTagStyle */
export const MOMENT_TAG_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(MOMENT_TAG_STYLES).map(([k, v]) => [k, v.dot]),
);

export function getMomentTagStyle(tagType: string): MomentTagStyle {
  return MOMENT_TAG_STYLES[tagType] || MOMENT_TAG_STYLES.task;
}

export function formatKeywordLabel(word: string): string {
  const trimmed = word.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function getKeywordFontSize(count: number, maxCount: number): number {
  const safeMax = Math.max(maxCount, 1);
  return 11 + (count / safeMax) * 7;
}

export const THEME_ACCENT_COLORS = [
  { bg: '#ede9fe', text: '#6d28d9', border: '#c4b5fd' },
  { bg: '#ffedd5', text: '#c2410c', border: '#fdba74' },
  { bg: KTZH_PRIMARY_LIGHT, text: KTZH_PRIMARY_DEEP, border: 'rgba(28, 155, 215, 0.35)' },
  { bg: '#e0f2fe', text: '#0369a1', border: '#7dd3fc' },
  { bg: '#fce7f3', text: '#be185d', border: '#f9a8d4' },
];

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function highlightWords(text: string, keywords: string[], color: string): string {
  const stems = new Set(
    keywords.filter(Boolean).map(keyword => keywordStem(keyword)).filter(Boolean),
  );
  if (!stems.size) return escapeHtml(text);

  const markStyle = `background:${color}50;color:${color};border-radius:3px;padding:0 3px;font-weight:500`;
  return escapeHtml(text).replace(
    /[а-яё]{4,}/gi,
    (word) => (
      stems.has(keywordStem(word))
        ? `<mark style="${markStyle}">${word}</mark>`
        : word
    ),
  );
}

export function getReplicaBadge(
  text: string,
  keywords: string[],
): { label: string; isDispute: boolean } {
  const lower = text.toLowerCase();
  if (/спор|куда вы лез|кто здесь прав|недопустим|претенз|обвин/i.test(lower)) {
    return { label: 'спор', isDispute: true };
  }
  for (const kw of keywords) {
    if (textContainsKeyword(text, kw)) {
      return { label: kw, isDispute: false };
    }
  }
  return { label: keywords[0] || '', isDispute: false };
}

const KEYWORD_SUFFIXES = [
  'ения', 'ания', 'ования', 'ований', 'ений', 'ами', 'ями', 'ого', 'ему', 'ому',
  'ов', 'ев', 'ам', 'ям', 'ах', 'ях', 'ом', 'ем', 'ой', 'ей', 'ую', 'юю',
  'ые', 'ие', 'ий', 'ый', 'ая', 'ое', 'ых', 'их', 'а', 'я', 'у', 'ю', 'е', 'и', 'ы', 'о',
];

export function keywordStem(word: string): string {
  const w = word.toLowerCase().trim();
  if (!w) return '';
  for (const suffix of KEYWORD_SUFFIXES) {
    if (w.endsWith(suffix) && w.length - suffix.length >= 4) {
      return w.slice(0, -suffix.length);
    }
  }
  return w;
}

function extractRussianWords(text: string): string[] {
  return (text || '').toLowerCase().match(/[а-яё]{4,}/g) || [];
}

export function textContainsKeyword(text: string, keyword: string): boolean {
  const targetStem = keywordStem(keyword);
  if (!targetStem) return false;
  return extractRussianWords(text).some(word => keywordStem(word) === targetStem);
}

export function countKeywordMentions(text: string, keyword: string): number {
  const targetStem = keywordStem(keyword);
  if (!targetStem) return 0;
  return extractRussianWords(text).filter(word => keywordStem(word) === targetStem).length;
}

export function segmentMatchesThemeWords(text: string, words: string[]): boolean {
  if (!text?.trim()) return false;
  return words.some(word => textContainsKeyword(text, word));
}

function scoreThemeSegment(text: string, words: string[]): number {
  let score = 0;
  for (const word of words) {
    score += countKeywordMentions(text, word);
  }
  return score;
}

function formatReplicaTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export type ThemeReplica = {
  segment: SpeechSegment;
  timeLabel: string;
  speaker: string;
  badge: { label: string; isDispute: boolean };
};

export function buildThemeReplicas(
  segments: SpeechSegment[],
  themeKeywords: ThemeKeyword[],
  limit = 5,
): ThemeReplica[] {
  const words = themeKeywords.map(k => k.word).filter(Boolean);
  if (!words.length) return [];

  const candidates = segments
    .filter(seg => seg.text?.trim() && segmentMatchesThemeWords(seg.text, words))
    .map(seg => ({
      segment: seg,
      speaker: getSpeakerName(seg),
      startSec: getSegmentStartSec(seg),
      score: scoreThemeSegment(seg.text || '', words),
    }))
    .sort((a, b) => b.score - a.score || a.startSec - b.startSec);

  const picked: typeof candidates = [];
  const usedSpeakers = new Set<string>();

  for (const item of candidates) {
    if (picked.length >= limit) break;
    if (usedSpeakers.has(item.speaker) && picked.length < limit - 1) continue;
    picked.push(item);
    usedSpeakers.add(item.speaker);
  }

  if (picked.length < limit) {
    for (const item of candidates) {
      if (picked.length >= limit) break;
      if (picked.some(p => p.segment.id === item.segment.id)) continue;
      picked.push(item);
    }
  }

  return picked
    .sort((a, b) => a.startSec - b.startSec)
    .map(item => ({
      segment: item.segment,
      timeLabel: formatReplicaTime(item.startSec),
      speaker: item.speaker,
      badge: getReplicaBadge(item.segment.text || '', words),
    }));
}

export function buildKeywordReplicas(
  segments: SpeechSegment[],
  word: string,
): ThemeReplica[] {
  const cleaned = word.trim();
  if (!cleaned) return [];

  return segments
    .filter(seg => seg.text?.trim() && textContainsKeyword(seg.text, cleaned))
    .sort((a, b) => getSegmentStartSec(a) - getSegmentStartSec(b))
    .map(seg => ({
      segment: seg,
      timeLabel: formatReplicaTime(getSegmentStartSec(seg)),
      speaker: getSpeakerName(seg),
      badge: getReplicaBadge(seg.text || '', [cleaned]),
    }));
}

export function countKeywordReplicas(
  segments: SpeechSegment[],
  word: string,
): number {
  return buildKeywordReplicas(segments, word).length;
}

export function filterSegmentsByThemeWords(
  segments: SpeechSegment[],
  words: string[],
): SpeechSegment[] {
  const cleaned = words.map(w => w.trim()).filter(Boolean);
  if (!cleaned.length) return segments;
  return segments.filter(seg => segmentMatchesThemeWords(seg.text || '', cleaned));
}

const FALLBACK_THEME_RULES = [
  {
    label: 'Обязательства',
    icon: 'checklist',
    color: '#7c3aed',
    stems: ['должн', 'рамк', 'согласн'],
    insight: 'Самая частая тема — фиксация кто что должен сделать, в т.ч. в рамках договорённостей.',
  },
  {
    label: 'Открытые вопросы',
    icon: 'help-circle',
    color: '#ea580c',
    stems: ['вопрос', 'данн'],
    insight: 'Много вопросов по данным и процессам — ответы не всегда зафиксированы.',
  },
  {
    label: 'Технические задачи',
    icon: 'plug-connected',
    color: '#0d9488',
    stems: ['доступ', 'систем', 'интегра', 'тест', 'ресурс', 'инфраструкт'],
    insight: 'Параллельно идёт техническая работа: API, доступ, тестирование, инфраструктура.',
  },
];

export function buildFallbackThemes(keywords: KeywordStat[]): KeywordTheme[] {
  const used = new Set<string>();
  const themes: KeywordTheme[] = [];

  for (const rule of FALLBACK_THEME_RULES) {
    const matched = keywords.filter(kw => {
      const stem = kw.word.slice(0, 5).toLowerCase();
      return rule.stems.some(s => stem.startsWith(s) || kw.word.toLowerCase().includes(s));
    }).filter(kw => {
      if (used.has(kw.word)) return false;
      used.add(kw.word);
      return true;
    });

    if (!matched.length) continue;

    themes.push({
      label: rule.label,
      icon: rule.icon,
      color: rule.color,
      mention_count: matched.reduce((sum, k) => sum + k.count, 0),
      insight: rule.insight,
      keywords: matched.map(k => ({ word: k.word, count: k.count })),
    });
  }

  return themes;
}

export function normalizeThemes(
  themes: KeywordTheme[] | undefined,
  keywords: KeywordStat[],
): KeywordTheme[] {
  if (themes?.length) {
    const normalized = themes.map((theme, index) => {
      const accent = THEME_ACCENT_COLORS[index % THEME_ACCENT_COLORS.length];
      const kwList = (theme.keywords || []).map(kw => (
        typeof kw === 'string'
          ? { word: kw, count: keywords.find(k => k.word === kw)?.count || 1 }
          : kw
      ));
      return {
        ...theme,
        color: theme.color || accent.text,
        insight: theme.insight || theme.summary || '',
        keywords: kwList,
        mention_count: theme.mention_count || kwList.reduce((s, k) => s + k.count, 0),
      };
    });
    if (normalized.some(t => t.keywords.length > 0)) {
      return normalized;
    }
  }
  return buildFallbackThemes(keywords);
}
